/**
 * Save / load — docs/04 §5.
 *
 * A save stores IDs and events, never content. That is what lets a content patch
 * ship without invalidating saves, and what makes an unknown ID a recoverable
 * warning instead of a crash.
 */

import { EventLog, type GameEvent } from './events.js';
import { createGameState, type GameState } from './state.js';
import type { ClassId, NpcId } from './types.js';
import type { RngStream } from './rng.js';
import type { DiceState } from './dice.js';
import type { NpcMemory } from './npc.js';

export const SAVE_SCHEMA_VERSION = 2;

export interface SaveMeta {
  readonly schemaVersion: number;
  readonly runSeed: string;
  readonly classId: ClassId;
  readonly playerName: string;
  readonly createdAtMs: number;
  readonly updatedAtMs: number;
  readonly playtimeMs: number;
  readonly tick: number;
  /** Written for diagnostics only; never used to make gameplay decisions. */
  readonly engineVersion: string;
}

export interface SaveFile {
  readonly meta: SaveMeta;
  /** pivotal+ in full, plus retained notable events. */
  readonly events: readonly GameEvent[];
  readonly snapshot: Snapshot;
  readonly aggregates: Readonly<Record<string, number>>;
}

/** Derived state at save time. Recomputable from events, stored to skip replay cost. */
export interface Snapshot {
  readonly currentRoom: string | null;
  readonly rng: Readonly<Record<RngStream, number>>;
  readonly dice: DiceState;
  readonly player: GameState['player'];
  readonly inventory: GameState['inventory'];
  readonly deck: GameState['deck'];
  readonly npcs: Readonly<Record<NpcId, NpcMemory>>;
  readonly flags: readonly string[];
  readonly knownFacts: readonly string[];
  readonly discoveredSecrets: readonly string[];
  readonly foundEasterEggs: readonly string[];
  readonly clearedRooms: readonly string[];
  readonly killedMonsters: Readonly<Record<string, number>>;
  readonly sparedMonsters: readonly string[];
  readonly activeQuests: readonly string[];
  readonly completedQuests: readonly string[];
  readonly chronicle: GameState['chronicle'];
  readonly ended: GameState['ended'];
}

export const ENGINE_VERSION = '0.1.0';

export function makeSnapshot(state: GameState, rng: Record<RngStream, number>): Snapshot {
  return {
    currentRoom: state.currentRoom,
    rng,
    dice: structuredCloneSafe(state.dice),
    player: structuredCloneSafe(state.player),
    inventory: structuredCloneSafe(state.inventory),
    deck: structuredCloneSafe(state.deck),
    npcs: structuredCloneSafe(state.npcs),
    flags: [...state.world.flags],
    knownFacts: [...state.world.knownFacts],
    discoveredSecrets: [...state.world.discoveredSecrets],
    foundEasterEggs: [...state.world.foundEasterEggs],
    clearedRooms: [...state.world.clearedRooms],
    killedMonsters: { ...state.world.killedMonsters },
    sparedMonsters: [...state.world.sparedMonsters],
    activeQuests: [...state.world.activeQuests],
    completedQuests: [...state.world.completedQuests],
    chronicle: structuredCloneSafe(state.chronicle),
    ended: state.ended ? { ...state.ended } : null,
  };
}

/** JSON round-trip clone. Avoids `structuredClone` so the engine runs on older runtimes. */
function structuredCloneSafe<T>(v: T): T {
  return JSON.parse(JSON.stringify(v)) as T;
}

export function restoreSnapshot(state: GameState, snap: Snapshot): void {
  state.currentRoom = snap.currentRoom;
  state.dice = structuredCloneSafe(snap.dice) as DiceState;
  state.player = structuredCloneSafe(snap.player);
  state.inventory = structuredCloneSafe(snap.inventory);
  state.deck = structuredCloneSafe(snap.deck);
  state.npcs = structuredCloneSafe(snap.npcs) as Record<NpcId, NpcMemory>;
  state.world.flags = new Set(snap.flags);
  state.world.knownFacts = new Set(snap.knownFacts);
  state.world.discoveredSecrets = new Set(snap.discoveredSecrets);
  state.world.foundEasterEggs = new Set(snap.foundEasterEggs);
  state.world.clearedRooms = new Set(snap.clearedRooms);
  state.world.killedMonsters = { ...snap.killedMonsters };
  state.world.sparedMonsters = new Set(snap.sparedMonsters);
  state.world.activeQuests = new Set(snap.activeQuests);
  state.world.completedQuests = new Set(snap.completedQuests);
  state.chronicle = structuredCloneSafe(snap.chronicle);
  state.ended = snap.ended ? { ...snap.ended } : null;
}

export interface LoadResult {
  readonly state: GameState;
  readonly log: EventLog;
  readonly meta: SaveMeta;
  readonly rng: Readonly<Record<RngStream, number>>;
  /** IDs present in the save that the current content build does not define. */
  readonly unresolved: readonly string[];
  readonly migrationsApplied: readonly string[];
}

export interface ContentResolver {
  hasItem(id: string): boolean;
  hasCard(id: string): boolean;
  hasNpc(id: string): boolean;
  hasRoom(id: string): boolean;
}

export class SaveError extends Error {
  constructor(
    message: string,
    readonly code: 'malformed' | 'unsupportedVersion',
  ) {
    super(message);
    this.name = 'SaveError';
  }
}

export function serializeSave(
  state: GameState,
  log: EventLog,
  meta: Omit<SaveMeta, 'schemaVersion' | 'engineVersion' | 'tick'>,
  rng: Record<RngStream, number>,
): SaveFile {
  return {
    meta: {
      ...meta,
      schemaVersion: SAVE_SCHEMA_VERSION,
      engineVersion: ENGINE_VERSION,
      tick: state.world.tick,
    },
    events: log.all(),
    snapshot: makeSnapshot(state, rng),
    aggregates: { ...state.world.aggregates },
  };
}

/**
 * Load a save, migrating forward and quarantining unknown IDs.
 *
 * Unknown IDs are stripped from the live state and reported rather than throwing:
 * a player whose save references a card removed in a patch should lose the card,
 * not the run.
 */
export function deserializeSave(raw: unknown, resolver?: ContentResolver): LoadResult {
  if (typeof raw !== 'object' || raw === null) {
    throw new SaveError('Save file is not an object', 'malformed');
  }
  const file = raw as Partial<SaveFile>;
  if (!file.meta || typeof file.meta.schemaVersion !== 'number') {
    throw new SaveError('Save file has no schemaVersion', 'malformed');
  }
  if (file.meta.schemaVersion > SAVE_SCHEMA_VERSION) {
    throw new SaveError(
      `Save schema v${file.meta.schemaVersion} is newer than this build (v${SAVE_SCHEMA_VERSION}). Update the game.`,
      'unsupportedVersion',
    );
  }

  /*
   * 여기까지는 `meta` 만 확인했다. 그 다음 줄부터 `snapshot` 과 `events` 를
   * 그냥 파고들었기 때문에, 잘린 세이브는 `SaveError('malformed')` 대신
   * 날것의 TypeError 를 던졌다 — 호출부는 그걸 "불러오기 실패"로 안내할 수 없다.
   */
  if (typeof file.snapshot !== 'object' || file.snapshot === null) {
    throw new SaveError('Save file has no snapshot', 'malformed');
  }
  if (!Array.isArray(file.events)) {
    throw new SaveError('Save file has no event log', 'malformed');
  }
  if (file.aggregates !== undefined && (typeof file.aggregates !== 'object' || file.aggregates === null)) {
    throw new SaveError('Save file has a malformed aggregates block', 'malformed');
  }

  const { file: migrated, applied } = migrate(file as SaveFile);

  const state = createGameState(migrated.meta.runSeed, migrated.meta.classId, migrated.meta.playerName);
  restoreSnapshot(state, migrated.snapshot);
  state.world.aggregates = { ...migrated.aggregates };
  state.world.tick = migrated.meta.tick;

  const log = new EventLog();
  for (const e of migrated.events) {
    // Re-append preserves seq ordering without re-deriving state from scratch.
    (log as unknown as { events: GameEvent[]; nextSeq: number }).events.push(e);
  }
  const maxSeq = migrated.events.reduce((m, e) => Math.max(m, e.seq), 0);
  (log as unknown as { nextSeq: number }).nextSeq = maxSeq + 1;

  const unresolved: string[] = [];
  if (resolver) {
    state.inventory.items = state.inventory.items.filter((id) => {
      if (resolver.hasItem(id)) return true;
      unresolved.push(id);
      return false;
    });
    const filterCards = (pile: string[]): string[] =>
      pile.filter((id) => {
        // Transformed cards carry `base+overlay`; validate against the base id.
        const base = id.split('+')[0]!;
        if (resolver.hasCard(base)) return true;
        unresolved.push(id);
        return false;
      });
    state.deck.cards = filterCards(state.deck.cards);
    state.deck.drawPile = filterCards(state.deck.drawPile);
    state.deck.hand = filterCards(state.deck.hand);
    state.deck.discard = filterCards(state.deck.discard);
    for (const npcId of Object.keys(state.npcs)) {
      if (!resolver.hasNpc(npcId)) {
        unresolved.push(npcId);
        delete state.npcs[npcId];
      }
    }
    if (state.currentRoom && !resolver.hasRoom(state.currentRoom)) {
      unresolved.push(state.currentRoom);
      state.currentRoom = null;
    }
  }

  return {
    state,
    log,
    meta: migrated.meta,
    rng: migrated.snapshot.rng,
    unresolved: [...new Set(unresolved)],
    migrationsApplied: applied,
  };
}

/**
 * Forward-only migrations. Each step upgrades exactly one version.
 * Never edit an existing migration after release — add a new one.
 */
type Migration = { readonly from: number; readonly name: string; readonly up: (f: SaveFile) => SaveFile };

const MIGRATIONS: readonly Migration[] = [
  {
    from: 1,
    name: 'v1->v2: add dice.bankCapacity and dice.faceTally',
    up: (f) => ({
      ...f,
      meta: { ...f.meta, schemaVersion: 2 },
      snapshot: {
        ...f.snapshot,
        dice: {
          ...f.snapshot.dice,
          bankCapacity: f.snapshot.dice.bankCapacity ?? 3,
          faceTally: f.snapshot.dice.faceTally ?? {},
        },
      },
    }),
  },
];

function migrate(file: SaveFile): { file: SaveFile; applied: string[] } {
  let current = file;
  const applied: string[] = [];
  let guard = 0;
  while (current.meta.schemaVersion < SAVE_SCHEMA_VERSION) {
    if (guard++ > 32) throw new SaveError('Migration loop detected', 'malformed');
    const step = MIGRATIONS.find((m) => m.from === current.meta.schemaVersion);
    if (!step) {
      throw new SaveError(
        `No migration from schema v${current.meta.schemaVersion} to v${SAVE_SCHEMA_VERSION}`,
        'unsupportedVersion',
      );
    }
    current = step.up(current);
    applied.push(step.name);
  }
  return { file: current, applied };
}
